import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AbstractControl, FormControl, ValidatorFn } from '@angular/forms';
import { MatTableDataSource } from '@angular/material/table';
import { JwtHelperService } from '@auth0/angular-jwt';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, lastValueFrom, map } from 'rxjs';
import { environment } from 'src/environments/environment';
import { Activity } from '../model/activity.model';
import { IdentificationFields, NoiseRecords, PermissionChangeBody, RemTimeColourCode } from '../model/common.model';
import { UserDetails, Users } from '../model/userrole.model';
import { Device } from '../model/device.model';

@Injectable({
  providedIn: 'root',
})
export class CommonService {
  backend_url = '';
  nonce = '';
  username = '';
  role = '';
  deviceId = '';
  filename = '';

  lang = navigator.language;
  noiseMainTypes: any = [];
  noiseSubTypes: any = new Object();
  isNoiseMainTypeDisabled: boolean = false;
  isNoiseSubTypeDisabled: boolean = false;
  subCode_customerCode: any = new Object();
  customerGuides: any = new Object();
  subCode_serviceGuide: any = new Object();
  noiseMainCodeValue: any = new Object();
  noiseSubCodeValue: any = new Object();
  serviceGuides: any = new Object();
  aiAutomationInfo: any = new Object();
  aiLabels: any = new Object();
  undecidedSubLabel: any = '';
  userRole: FormControl = new FormControl('');
  approvalSentStatusCheck = false;
  approvalStatus = '';
  dataSource = new MatTableDataSource<Users | Activity | NoiseRecords>();
  isLoaderEnabled: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  loaderMessage: BehaviorSubject<string> = new BehaviorSubject<string>('');
  deviceType: BehaviorSubject<string> = new BehaviorSubject<string>('00');
  devices = [];
  reason: FormControl = new FormControl('');
  showSoundAnalysisResult: boolean = false;
  HumanVoice: boolean = false;
  delete: boolean = false;
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  securityPledgeSign = new BehaviorSubject<string>('');
  securityPledgeSign$ = this.securityPledgeSign.asObservable();
  setSecurityPledgeSign(value: string) {
    this.securityPledgeSign.next(value);
  }
  constructor(
    private httpClient: HttpClient,
    private toastrService: ToastrService,
    public jwtService: JwtHelperService
  ) {
    this.generateNonce();
    let auth_params: any = '?client_id=' + environment.SSO_CLIENT_ID;
    auth_params += '&redirect_uri=' + encodeURIComponent(environment.SSO_REDIRECT_URI);
    auth_params += '&response_mode=fragment';
    auth_params += '&response_type=id_token';
    auth_params += '&scope=openid+profile';
    auth_params += '&nonce=' + this.nonce;
    localStorage.setItem('nonce', String(this.nonce));
    this.backend_url = environment.SSO_URL + auth_params;
  }

  generateNonce() {
    const array = new Uint8Array(16); // 128 bits for a 16-byte nonce
    crypto.getRandomValues(array);
    this.nonce = this.bytesToHex(array);
  }
  bytesToHex(bytes: Uint8Array): string {
    return Array.from(bytes)
      .map(byte => byte.toString(16).padStart(2, '0'))
      .join('');
  }
  decodeToken(token: string) {
    try {
      const decodedToken = this.jwtService.decodeToken(token);
      return decodedToken;
    } catch (error) {
      this.displayError('Error decoding token');
      return null;
    }
  }
  isTokenExpired(token: any): boolean | Promise<boolean> {
    if (this.decodeToken(token)) return this.jwtService.isTokenExpired(token);
    return true;
  }
  logOut() {
    this.showLoader('Logging out...');
    localStorage.clear();
    this.role = '';
    this.username = '';
    window.location.href = environment.SSO_LOGOUT_URL;
  }

  /*When loader is enabled, there'll be no popup
  and only loaderMessage must be shown
  */
  showLoader(loaderMessage: string) {
    this.isLoaderEnabled.next(true);
    this.loaderMessage.next(loaderMessage);
  }
  //When loader is disabled, popup message should be shown
  hideLoader(popupMessage: string, isError: boolean) {
    this.isLoaderEnabled.next(false);
    this.loaderMessage.next('');
    if (isError) this.displayError(popupMessage);
    else this.displaySuccess(popupMessage);
  }
  displayError(message: string) {
    this.toastrService.error(message, '', {
      positionClass: 'toast-bottom-center',
      timeOut: 4000,
      messageClass: 'center-msg',
    });
  }

  displayDisabled(message: string) {
    this.toastrService.info(message, '', {
      positionClass: 'toast-bottom-center',
      timeOut: 3000,
      messageClass: 'center-msg',
    });
  }
  displaySuccess(message: string) {
    this.toastrService.success(message, '', {
      positionClass: 'toast-bottom-center',
      timeOut: 3000,
      messageClass: 'center-msg',
    });
  }
  forbiddenTextValidator(forbiddenText: string[]): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      if (!control.value) {
        return null; // Return null if control has no value
      }

      const containsForbiddenText = forbiddenText.some(text => control.value.includes(text));

      return containsForbiddenText ? { forbiddenText: { value: control.value } } : null;
    };
  }
  getNavigatorLang() {
    if (this.lang.includes('en')) this.lang = 'en';
    else if (this.lang.includes('ko')) this.lang = 'ko';
    return this.lang;
  }

  setRemDaysColour(noiseRecord: NoiseRecords) {
    let remTimeColour = '';
    const ai_result =
      (!noiseRecord['noise_type_main'] || !noiseRecord['noise_type_main'].includes(IdentificationFields.Undecided)) &&
      noiseRecord['classification_accuracy'] === IdentificationFields.Undecided &&
      noiseRecord['registration'] === 'true';
    if (ai_result) {
      remTimeColour = noiseRecord['labelling'] === 'true' ? RemTimeColourCode.Resolved : RemTimeColourCode.Unresolved;
    } else {
      if (noiseRecord['registration'] === 'true') remTimeColour = RemTimeColourCode.Resolved;
      else {
        const remainingTime =
          (new Date(noiseRecord['analysis_deadline']).getTime() - new Date().getTime()) / (24 * 60 * 60 * 1000);
        if (remainingTime >= 2) remTimeColour = RemTimeColourCode.Unresolved;
        else if (remainingTime < 2 && remainingTime >= 1) remTimeColour = RemTimeColourCode.LessThanTwoDays;
        else if (remainingTime < 1 && remainingTime > 0) remTimeColour = RemTimeColourCode.LessThanOneDay;
        else remTimeColour = RemTimeColourCode.DeadlineExceeded;
      }
    }
    return remTimeColour;
  }

  fetchRecords(queryParams: string): Promise<any> {
    const url = `noiseportal/fetch?${queryParams}`;
    return lastValueFrom(this.httpClient.get<Promise<any>>(url));
  }
  fetchMainSubTypes(deviceType: string): Observable<object> {
    const dumpDataUrl = `../../assets/dump/${this.lang}/${deviceType}_dumps.json`;
    return this.httpClient.get(dumpDataUrl);
  }
  updateColumns(cols: any): Observable<any> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const url = `update/?username=${this.username}&timezone=${timezone}&role=${this.role}`;
    return this.httpClient.patch<any>(url, cols);
  }

  updateApi(cols: any, filename: any): Observable<any> {
    const url = `noiseportal/${filename}`;
    return this.httpClient.patch<any>(url, cols);
  }

  login(): Promise<UserDetails> {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const url = `noiseportal/user-info/?timezone=${timezone}`;
    return lastValueFrom(this.httpClient.get<UserDetails>(url));
  }

  getApprovalStatus(username: string): Promise<any> {
    const url = `noiseportal/approval-status/?username=${username}`;
    return lastValueFrom(this.httpClient.get<any>(url));
  }

  sendApprovalRequest(body: any) {
    const url = `noiseportal/save-knox-approval/`;
    return this.httpClient.post(url, body);
  }

  getApprovalList(): Promise<any> {
    const url = `noiseportal/knox-apporval/?username=${this.username}`;
    return lastValueFrom(this.httpClient.get<any>(url));
  }

  getUserDetails(queryString: string): any {
    const url = `noiseportal/user-details/${queryString}`;
    return this.httpClient.get<any>(url);
  }

  getPermissionChange(user_id: string, status: string): any {
    const url = `noiseportal/permission-change/?username=${user_id}&role=admin&operation=${status}`;
    return this.httpClient.get<any>(url);
  }

  getUserLog(queryString: string): any {
    const url = `noiseportal/user-log/${queryString}`;
    return this.httpClient.get<any>(url);
  }

  getActivityLog(queryString: string, isUnusualActivity: boolean): any {
    const apiUrl = isUnusualActivity ? 'unusual-log' : 'user-log';
    const url = `noiseportal/${apiUrl}/${queryString}`;
    return this.httpClient.get<any>(url);
  }

  getFileDownload(payload: any): any {
    const url = `noiseportal/download/`;
    return this.httpClient.post<any>(url, payload);
  }

  editUserRole(body: any) {
    const url = `noiseportal/role-edit/`;
    return this.httpClient.patch(url, body);
  }

  assignUserRole(body: PermissionChangeBody, action: string) {
    const url = `noiseportal/${action == 'approve' ? 'role-approve/' : 'role-reject/'}`;
    return this.httpClient.patch(url, body);
  }

  deleteUserRole(body: any) {
    const url = 'noiseportal/role-delete/';
    return this.httpClient.patch(url, body);
  }

  updateAIAutomation(body: any) {
    const url = 'noiseportal/toggle/ai-automation';
    return this.httpClient.put(url, body);
  }

  fetchDevices(): Observable<Device[]> {
    const url = 'noiseportal/device/type/metadata';
    return this.httpClient.get<Device[]>(url);
  }

  fetchBulkDownload(payload: any): any {
    const url = `noiseportal/bulk/download`;
    return this.httpClient.post<any>(url, payload);
  }
  updateSecurityPledgeSign(payload: any): any {
    const url = 'noiseportal/accept-policy-terms/';
    return this.httpClient.patch<any>(url, payload);
  }
}
